// THE `team_service_probe` PAYLOAD — what the team session's run-start probe found
// about the services the checkout's committed policy declares (#4332).

import { z } from 'zod';
import { RepoServiceSchema } from '../../entities/runner/capabilities';

/** The status event name the team session emits the probe's row under. */
export const TEAM_SERVICE_PROBE_EVENT = 'team_service_probe' as const;

/**
 * Where the `services:` declaration was read from — the policy home that resolved
 * (`dz` / `root`), or why nothing was declared: no policy at either home (`none`), a
 * policy file that exists but could not be read (`unreadable`), or one the policy
 * parser refuses (`malformed`). Every non-home value means `required` is empty and
 * the session went on: the probe is FAIL-OPEN over the policy read, because "declares
 * no services" and "the mothership would have required none" are the same fact —
 * the claim gate reads `parsed.services` off the stored policy, and an unparseable
 * policy stores none.
 */
export const TeamServiceProbePolicySchema = z.enum([
  'dz',
  'root',
  'none',
  'unreadable',
  'malformed',
]);
export type TeamServiceProbePolicy = z.infer<typeof TeamServiceProbePolicySchema>;

/**
 * The run-start service probe's row: emitted on EVERY team session, before the refusal
 * when there is one. Today it is the refusal's EVIDENCE only.
 *
 * NOT BUILT: the advert retraction (#4333, open). No mothership code consumes this row,
 * so a box whose declared service is down KEEPS its `service:*` advert: the advert
 * write is union-only and has no way down, and the box goes on claiming repos that
 * need that service and refusing them `service_unreachable` (row 09-49). When #4333
 * lands, its consumer must take the runner from the run's established owner, never
 * from this payload (so a box can only retract its own advert), and validate each name
 * against {@link RepoServiceSchema}, because the advert is a dispatch-gate column and
 * the box is untrusted input. `unreachable ⊆ required` holds by construction on the
 * producer only.
 */
export const TeamServiceProbePayloadSchema = z
  .object({
    event: z.literal(TEAM_SERVICE_PROBE_EVENT),
    policy: TeamServiceProbePolicySchema,
    /** Every service the committed policy declares; empty when the policy declares none. */
    required: z.array(RepoServiceSchema),
    /** The declared services that accepted no TCP connect on loopback. Empty is the pass. */
    unreachable: z.array(RepoServiceSchema),
    duration_ms: z.number().int().nonnegative(),
  })
  // STRICT, like the other hive payload schemas: the producer is an untrusted box, and
  // a row carrying an unrecognized key must refuse at the read boundary, never validate
  // silently (and, once #4333 builds one, never reach the advert retraction).
  .strict();
export type TeamServiceProbePayload = z.infer<typeof TeamServiceProbePayloadSchema>;
